import React, { useMemo, useState } from 'react';

/**
 * Render a deduped, prioritized list of legal places to read this manhua.
 * AniList sometimes returns multiple language variants of the same publisher
 * (Webtoons EN/ES/DE/FR/...), so we group by site and show language sub-pills.
 *
 * `mode='prominent'` renders the big CTA used in the "no chapters" empty state;
 * `mode='compact'` renders a thinner row to sit alongside MangaDex chapters.
 *
 * Future i18n: when we open new geos we can pre-filter by user locale before
 * calling this; the data is already language-tagged.
 */

// Hand-tuned ordering: ones with the most reliable English reading experience
// first, then by reach. Anything not listed sorts after these.
const SITE_PRIORITY = [
  'WEBTOON', 'Webtoons', 'Webtoon',
  'Tappytoon', 'Lezhin', 'Manta', 'Tapas', 'INKR',
  'Comikey', 'MangaPlus', 'Manga Plus',
  'Pocket Comics', 'BookWalker', 'Bookwalker', 'ComiXology',
  'Naver Webtoon', 'Kakao Webtoon', 'KakaoPage', 'Kakao Page',
  'Piccoma', 'Bilibili', 'KuaiKan Manhua', 'Naver Series',
];

const SITE_EMOJI = {
  WEBTOON: '🟢', Webtoons: '🟢', Webtoon: '🟢',
  Tappytoon: '💜', Lezhin: '⚫️', Manta: '🌊', Tapas: '🟠',
  Comikey: '🔵', MangaPlus: '🟥', INKR: '🟧',
};

function groupBySite(links) {
  const map = new Map();
  for (const l of links || []) {
    if (!l?.site || !l?.url) continue;
    const key = l.site;
    if (!map.has(key)) map.set(key, { site: key, langs: [] });
    map.get(key).langs.push({ language: l.language || 'Unknown', url: l.url });
  }
  // Sort sites by priority list, then alpha
  return Array.from(map.values()).sort((a, b) => {
    const pa = SITE_PRIORITY.indexOf(a.site);
    const pb = SITE_PRIORITY.indexOf(b.site);
    if (pa === -1 && pb === -1) return a.site.localeCompare(b.site);
    if (pa === -1) return 1;
    if (pb === -1) return -1;
    return pa - pb;
  });
}

function pickEnglishOrFirst(langs) {
  // English first if available; AniList uses null for 'untagged' which is
  // typically the canonical (often English) URL on the publisher's main site.
  const en = langs.find((l) => l.language === 'English' || l.language === 'Unknown');
  return en || langs[0];
}

const SiteButton = ({ site, langs, prominent }) => {
  const [expanded, setExpanded] = useState(false);
  const primary = pickEnglishOrFirst(langs);
  const emoji = SITE_EMOJI[site] || '🌐';

  const baseCls = prominent
    ? 'inline-flex items-center gap-2 px-4 py-2.5 bg-rose-600 hover:bg-rose-500 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm'
    : 'inline-flex items-center gap-2 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-xs font-medium rounded-md transition-colors border border-zinc-700';

  return (
    <div className="inline-flex flex-col items-start">
      <a href={primary.url} target="_blank" rel="noopener noreferrer" className={baseCls}>
        <span>{emoji}</span>
        <span>{site}</span>
        <span className="opacity-70">↗</span>
      </a>
      {langs.length > 1 && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 text-[10px] text-zinc-500 hover:text-zinc-300"
        >
          {expanded ? 'hide' : `+${langs.length - 1} more language${langs.length - 1 > 1 ? 's' : ''}`}
        </button>
      )}
      {expanded && (
        <div className="mt-1 flex flex-wrap gap-1 max-w-xs">
          {langs.map((l, i) => (
            <a
              key={i}
              href={l.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] px-2 py-0.5 bg-zinc-900 border border-zinc-700 rounded text-zinc-300 hover:text-rose-300"
            >
              {l.language}
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

const LegalSourcesList = ({ links, mode = 'prominent', heading }) => {
  const grouped = useMemo(() => groupBySite(links), [links]);
  if (!grouped.length) return null;

  const prominent = mode === 'prominent';
  return (
    <div className={prominent ? 'mt-2' : 'mt-6'}>
      {heading && (
        <h3 className={`uppercase tracking-wider mb-2 ${prominent ? 'text-xs text-zinc-400' : 'text-xs text-zinc-500'}`}>
          {heading}
        </h3>
      )}
      <div className="flex flex-wrap gap-2">
        {grouped.map((g) => (
          <SiteButton key={g.site} site={g.site} langs={g.langs} prominent={prominent} />
        ))}
      </div>
    </div>
  );
};

export default LegalSourcesList;
